import { Routes } from '@angular/router';
import { SuperuserGuard } from './_guards/superuser.guard';
import { supportWorkspaceGuard } from './_guards/support-workspace.guard';

import { LoginComponent } from './views/login/login.component';

import { AuthGuard, AuthGuardChild } from './_guards/auth-guard.service';
import { LoginGuard } from './_guards/login-guard.service';
import { DashboardComponent } from './views/dashboard/dashboard.component';
import { SiteAppointmentsReadGuard } from './_guards/site-appointments-read.guard';
import { PatientsComponent } from './views/patients/patients.component';
import { TasksRealComponent } from './views/tasks-real/tasks-real.component';
import { TaskDetailRealComponent } from './views/tasks-real/task-detail-real/task-detail-real.component';

import { AppointmentsComponent } from './views/appointments/appointments.component';
import { RedirectComponent } from './views/admin/redirect/redirect.component';
import { IsAdminGuardGuard } from './_guards/is-admin-guard.guard';
import { StatsGuard } from './_guards/stats.guard';

// Statistics Module
import { StatisticsComponent } from './views/statistics/statistics.component';
import { StatsDashboardComponent } from './views/statistics/dashboard/dashboard.component';
import { StatsRevenueComponent } from './views/statistics/revenue/revenue.component';
import { StatsTreatmentsComponent } from './views/statistics/treatments/treatments.component';
import { StatsPatientsComponent } from './views/statistics/patients/patients.component';
import { StatsAppointmentsComponent } from './views/statistics/appointments/appointments.component';

import { ExportAppointmentsComponent } from './views/admin/admin-data/export/export-appointments/export-appointments.component';
import { ExportPatientsComponent } from './views/admin/admin-data/export/export-patients/export-patients.component';
import { userPermissionsResolver } from './_resolvers/permission.resolver';
import { BusinessAnalysisComponent } from './views/business-analysis/business-analysis.component';
import { FormUserComponent } from './components/forms/form-user/form-user.component';
import { TableUsersComponent } from './components/tables/table-users/table-users.component';
import { FormSiteComponent } from './components/forms/form-site/form-site.component';
import { TableSitesComponent } from './components/tables/table-sites/table-sites.component';
import { TablePatientsComponent } from './components/tables/table-patients/table-patients.component';
import { FormPatientComponent } from './components/forms/form-patient/form-patient.component';
import { FormMetadataPatientsComponent } from './components/forms/form-metadata-patients/form-metadata-patients.component';
import { TableMetadataPatientsComponent } from './components/tables/table-metadata-patients/table-metadata-patients.component';
import { TableProductsComponent } from './components/tables/table-products/table-products.component';
import { FormProductsComponent } from './components/forms/form-products/form-products.component';
import { TableCategoriesProductsComponent } from './components/tables/table-categories-products/table-categories-products.component';
import { FormCategoryProductsComponent } from './components/forms/form-category-products/form-category-products.component';
import { TableJobsComponent } from './components/tables/table-jobs/table-jobs.component';
import { FormJobComponent } from './components/forms/form-job/form-job.component';
import { TableTreatmentsComponent } from './components/tables/table-treatments/table-treatments.component';
import { FormTreatmentComponent } from './components/forms/form-treatment/form-treatment.component';
import { TableCategoriesTreatmentsComponent } from './components/tables/table-categories-treatments/table-categories-treatments.component';
import { FormCategoryTreatmentsComponent } from './components/forms/form-category-treatments/form-category-treatments.component';
import { TableRolesComponent } from './components/tables/table-roles/table-roles.component';
import { FormRoleComponent } from './components/forms/form-role/form-role.component';
import { TableMarketingChannelsComponent } from './components/tables/table-marketing-channels/table-marketing-channels.component';
import { FormMarketingChannelComponent } from './components/forms/form-marketing-channel/form-marketing-channel.component';
import { TableLogsComponent } from './components/tables/table-logs/table-logs.component';
import { TableCustomersComponent } from './components/tables/table-customers/table-customers.component';
import { FormCustomerSignUpComponent } from './components/forms/form-customer-sign-up/form-customer-sign-up.component';
import { AdminBusinessCategoriesComponent } from './views/admin/admin-business-categories/admin-business-categories.component';
import { AdminCustomersComponent } from './views/admin/admin-customers/admin-customers.component';
import { AdminContactLeadsComponent } from './views/admin/admin-contact-leads/admin-contact-leads.component';
import { AdminGlobalPricingComponent } from './views/admin/admin-global-pricing/admin-global-pricing.component';
import { AdminCustomerAccountComponent } from './views/admin/admin-customer-account/admin-customer-account.component';
import { AdminSchedulesComponent } from './views/admin/admin-schedules/admin-schedules.component';
import { AdminRootComponent } from './views/admin/admin-root/admin-root.component';
import { AdminSectionComponent } from './views/admin/admin-section/admin-section.component';
import { AdminUserProfileComponent } from './views/admin/admin-user-profile/admin-user-profile.component';
import { AdminUserAccessComponent } from './views/admin/admin-user-access/admin-user-access.component';
import { AdminUserSecurityComponent } from './views/admin/admin-user-security/admin-user-security.component';
import { AdminUserLandingComponent } from './views/admin/admin-user-landing/admin-user-landing.component';
import { AdminUserEmployeeComponent } from './views/admin/admin-user-employee/admin-user-employee.component';
import { AdminUserInvitationsComponent } from './views/admin/admin-user-invitations/admin-user-invitations.component';
import { AdminRolePermissionsComponent } from './views/admin/admin-role-permissions/admin-role-permissions.component';
import { AdminRoleWidgetsComponent } from './views/admin/admin-role-widgets/admin-role-widgets.component';
import { AdminRoleDetailComponent } from './views/admin/admin-role-detail/admin-role-detail.component';
import { AdminRoleEditComponent } from './views/admin/admin-role-edit/admin-role-edit.component';
import { AdminUserDetailComponent } from './views/admin/admin-user-detail/admin-user-detail.component';
import { AdminPatientManageComponent } from './views/admin/admin-patient-manage/admin-patient-manage.component';
import { AdminTreatmentDetailComponent } from './views/admin/admin-treatment-detail/admin-treatment-detail.component';
import { AdminProductDetailComponent } from './views/admin/admin-product-detail/admin-product-detail.component';
import { AdminJobDetailComponent } from './views/admin/admin-job-detail/admin-job-detail.component';
import { AdminMarketingChannelDetailComponent } from './views/admin/admin-marketing-channel-detail/admin-marketing-channel-detail.component';
import { AdminTreatmentCategoryDetailComponent } from './views/admin/admin-treatment-category-detail/admin-treatment-category-detail.component';
import { AdminProductCategoryDetailComponent } from './views/admin/admin-product-category-detail/admin-product-category-detail.component';
import { AdminCalendarDetailComponent } from './views/admin/admin-calendar-detail/admin-calendar-detail.component';
import {
  adminCalendarResolver,
  adminClinicResolver,
  adminJobResolver,
  adminMarketingResolver,
  adminPatientResolver,
  adminProductCategoryResolver,
  adminProductResolver,
  adminRoleResolver,
  adminTreatmentCategoryResolver,
  adminTreatmentResolver,
  adminUserResolver,
} from './_resolvers/admin/admin-entity.resolver';

import { SalesComponent } from './views/sales/sales.component';
import { SalesListComponent } from './views/sales/sales-list/sales-list.component';
import { SalesStatsComponent } from './views/sales/sales-stats/sales-stats.component';
import { EntryComponent } from './views/entry/entry.component';
import { ClinicSelectorComponent } from './views/context/clinic-selector/clinic-selector.component';
import { NoClinicsComponent } from './views/context/no-clinics/no-clinics.component';
import { nonAdminPrefixGuard } from './_guards/non-admin-prefix.guard';
import { WorkspaceAccessGuard } from './_guards/workspace-access.guard';
import { OnlineBookingEntryGuard } from './_guards/online-booking-entry.guard';
import { PublicBookingComponent } from './views/public-booking/public-booking.component';
import { PublicBookingManageComponent } from './views/public-booking/public-booking-manage.component';
import { OnlineBookingShareComponent } from './views/online-booking-share/online-booking-share.component';
import { PatientSpaceLayoutComponent } from './views/patient-space/patient-space-layout.component';
import { PatientHomeComponent } from './views/patient-space/patient-home.component';
import { PatientAppointmentsComponent } from './views/patient-space/patient-appointments.component';
import { PatientClinicsComponent } from './views/patient-space/patient-clinics.component';
import { PatientPurchasesComponent } from './views/patient-space/patient-purchases.component';
import { PatientProfileComponent } from './views/patient-space/patient-profile.component';
import { PatientDiscoverComponent } from './views/patient-space/patient-discover.component';
import { NotFoundComponent } from './views/not-found/not-found.component';
import { AdminGlobalEmailAccountComponent } from './views/admin/admin-global-email-account/admin-global-email-account.component';

export const routes: Routes = [
  {
    path: 'demo/:sector',
    loadComponent: () => import('./views/demo-entry/demo-entry.component').then(m => m.DemoEntryComponent),
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./views/password-recovery/password-recovery.component').then(m => m.PasswordRecoveryComponent),
    data: { recoveryMode: 'request' },
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./views/password-recovery/password-recovery.component').then(m => m.PasswordRecoveryComponent),
    data: { recoveryMode: 'reset' },
  },
  {
    path: '',
    component: LoginComponent,
    canActivate: [LoginGuard],
    pathMatch: 'full',
  },
  {
    path: 'professionals',
    component: LoginComponent,
    canActivate: [LoginGuard],
  },
  {
    path: '404',
    component: NotFoundComponent,
  },
  {
    path: 'book/:customerIdentifier/:siteIdentifier/manage/:token',
    component: PublicBookingManageComponent,
  },
  {
    path: 'book/:customerIdentifier/:siteIdentifier',
    component: PublicBookingComponent,
  },
  {
    path: 'invitation/:token',
    loadComponent: () =>
      import('./views/invitation-landing/invitation-landing.component').then(
        (m) => m.InvitationLandingComponent,
      ),
  },
  {
    path: 'onboarding/:token',
    loadComponent: () =>
      import('./views/invitation-landing/invitation-landing.component').then(
        (m) => m.InvitationLandingComponent,
      ),
  },
  { path: 'entry', component: EntryComponent, canActivate: [AuthGuard] },
  {
    path: 'context/select-clinic',
    component: ClinicSelectorComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'context/no-clinics',
    component: NoClinicsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'patient',
    component: PatientSpaceLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      { path: 'home', component: PatientHomeComponent },
      { path: 'appointments', component: PatientAppointmentsComponent },
      { path: 'clinics', component: PatientClinicsComponent },
      { path: 'purchases', component: PatientPurchasesComponent },
      { path: 'discover', component: PatientDiscoverComponent },
      { path: 'profile', component: PatientProfileComponent },
    ],
  },
  {
    path: 'profile',
    component: PatientProfileComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'support',
    loadComponent: () => import('./views/support/support-tickets.component').then((m) => m.SupportTicketsComponent),
    canActivate: [AuthGuard, supportWorkspaceGuard],
  },
  {
    path: 'support/:id',
    loadComponent: () => import('./views/support/support-ticket-detail.component').then((m) => m.SupportTicketDetailComponent),
    canActivate: [AuthGuard, supportWorkspaceGuard],
  },
  {
    path: 'settings',
    component: NotFoundComponent,
  },
  {
    path: 'settings/:legacy',
    component: NotFoundComponent,
  },
  {
    path: 'settings/:legacy/:extra',
    component: NotFoundComponent,
  },
  {
    path: ':customer/:site/appointments',
    component: AppointmentsComponent,
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, SiteAppointmentsReadGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
  },
  {
    path: ':customer/:site/sales',
    component: SalesComponent,
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, SiteAppointmentsReadGuard, WorkspaceAccessGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
    data: {
      requiredModule: 'sales',
      requiredPermissionsAny: ['SALES.READ'],
    },
    children: [
      { path: '', component: SalesListComponent },
      { path: 'stats', component: SalesStatsComponent },
    ],
  },
  {
    path: ':customer/:site/tasks',
    component: TasksRealComponent,
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, WorkspaceAccessGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
    data: {
      requiredModule: 'tasks',
      requiredPermissionsAny: ['TASKS.READ'],
    },
  },
  {
    path: ':customer/:site/reminders',
    loadComponent: () =>
      import('./views/reminders/reminders.component').then(
        (m) => m.RemindersComponent,
      ),
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, WorkspaceAccessGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
    data: {
      requiredModule: 'reminders',
    },
  },
  {
    path: ':customer/:site/tasks/new',
    component: TaskDetailRealComponent,
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, WorkspaceAccessGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
    data: {
      requiredModule: 'tasks',
      requiredPermissionsAny: ['TASKS.CREATE'],
    },
  },
  {
    path: ':customer/:site/tasks/:taskKey',
    component: TaskDetailRealComponent,
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, WorkspaceAccessGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
    data: {
      requiredModule: 'tasks',
      requiredPermissionsAny: ['TASKS.READ'],
    },
  },
  {
    path: ':customer/:site/patients',
    component: PatientsComponent,
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, SiteAppointmentsReadGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
  },
  // Redirect old analytics route to new statistics module
  {
    path: ':customer/:site/analytics',
    canMatch: [nonAdminPrefixGuard],
    redirectTo: ':customer/:site/statistics',
    pathMatch: 'full',
  },
  // Statistics Module
  {
    path: ':customer/:site/statistics',
    component: StatisticsComponent,
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, StatsGuard, WorkspaceAccessGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
    data: {
      requiredModule: 'analytics',
      requiredPermissionsAny: ['STATISTICS.READ'],
    },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', component: StatsDashboardComponent },
      { path: 'revenue', component: StatsRevenueComponent },
      { path: 'treatments', component: StatsTreatmentsComponent },
      { path: 'patients', component: StatsPatientsComponent },
      { path: 'appointments', component: StatsAppointmentsComponent },
    ],
  },
  {
    path: ':customer/:site/online-booking',
    component: OnlineBookingShareComponent,
    canMatch: [nonAdminPrefixGuard],
    canActivate: [OnlineBookingEntryGuard, WorkspaceAccessGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
    data: {
      requiredModule: 'online_booking',
    },
  },
  {
    path: ':customer/patients/:id/details/:tab',
    loadComponent: () =>
      import('./views/patient/patient.component').then(
        (m) => m.PatientComponent,
      ),
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
  },
  {
    path: ':customer/patients/:id/details',
    loadComponent: () =>
      import('./views/patient/patient.component').then(
        (m) => m.PatientComponent,
      ),
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
  },
  {
    path: ':customer/:site/dashboard',
    component: DashboardComponent,
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, SiteAppointmentsReadGuard, WorkspaceAccessGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
    data: {
      requiredModule: 'dashboard',
      requiredPermissionsAny: ['DASHBOARD.READ'],
    },
  },
  {
    path: ':customer/:site/ai-assistant',
    loadComponent: () =>
      import('./views/ai-assistant/ai-assistant.component').then(
        (m) => m.AiAssistantComponent,
      ),
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, WorkspaceAccessGuard],
    resolve: { permissions: userPermissionsResolver },
    data: {
      requiredModule: 'ai',
      requiredPermissionsAny: ['AI_ASSISTANT.READ'],
    },
  },
  {
    path: ':customer/:site/incidents',
    loadComponent: () =>
      import('./views/incidents/incidents.component').then(
        (m) => m.IncidentsComponent,
      ),
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard, SiteAppointmentsReadGuard, WorkspaceAccessGuard],
    resolve: { permissions: userPermissionsResolver },
    data: {
      requiredModule: 'incidents',
      requiredPermissionsAny: ['INCIDENTS.READ'],
    },
  },
  {
    path: ':customer/business-analysis',
    component: BusinessAnalysisComponent,
    canMatch: [nonAdminPrefixGuard],
    canActivate: [AuthGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
  },
  {
    path: 'successful-login',
    redirectTo: 'entry',
    pathMatch: 'full',
  },
  {
    path: 'administration',
    component: RedirectComponent,
  },
  {
    path: 'administration/customers',
    component: AdminCustomersComponent,
    canActivate: [AuthGuard, IsAdminGuardGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
    children: [
      { path: '', component: TableCustomersComponent },
      { path: 'new', component: FormCustomerSignUpComponent },
      { path: 'categories', component: AdminBusinessCategoriesComponent },
      { path: 'pricing', component: AdminGlobalPricingComponent },
      { path: 'email-account', component: AdminGlobalEmailAccountComponent },
      { path: ':id/account', component: AdminCustomerAccountComponent },
      { path: 'list', redirectTo: '', pathMatch: 'full' },
      { path: 'create', redirectTo: 'new', pathMatch: 'full' },
    ],
  },
  {
    path: 'administration/contact-leads',
    component: AdminContactLeadsComponent,
    canActivate: [AuthGuard, IsAdminGuardGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
  },
  {
    path: 'administration/support',
    loadComponent: () => import('./views/support/support-tickets.component').then((m) => m.SupportTicketsComponent),
    canActivate: [AuthGuard, SuperuserGuard, supportWorkspaceGuard],
    data: { supportAdmin: true },
  },
  {
    path: 'administration/support/:id',
    loadComponent: () => import('./views/support/support-ticket-detail.component').then((m) => m.SupportTicketDetailComponent),
    canActivate: [AuthGuard, SuperuserGuard, supportWorkspaceGuard],
    data: { supportAdmin: true },
  },
  {
    path: 'administration/:customers_id',
    component: AdminRootComponent,
    canActivateChild: [AuthGuardChild],
    canActivate: [AuthGuard, IsAdminGuardGuard],
    resolve: {
      permissions: userPermissionsResolver,
    },
    children: [
      {
        path: '',
        redirectTo: 'users',
        pathMatch: 'full',
      },
      {
        path: 'sites',
        redirectTo: 'clinics',
        pathMatch: 'full',
      },
      {
        path: 'sites/list',
        redirectTo: 'clinics',
        pathMatch: 'full',
      },
      {
        path: 'sites/create',
        redirectTo: 'clinics/new',
        pathMatch: 'full',
      },
      {
        path: 'sites/:sites_id/config',
        redirectTo: 'clinics/:sites_id/general',
        pathMatch: 'full',
      },
      {
        path: 'overview',
        redirectTo: 'users',
        pathMatch: 'full',
      },
      {
        path: 'modules',
        loadComponent: () =>
          import('./views/admin/admin-subscription/admin-subscription.component').then(
            (m) => m.AdminSubscriptionComponent,
          ),
        data: {
          subscriptionTab: 'marketplace',
        },
      },
      {
        path: 'subscription',
        loadComponent: () =>
          import('./views/admin/admin-subscription/admin-subscription.component').then(
            (m) => m.AdminSubscriptionComponent,
          ),
        data: {
          subscriptionTab: 'subscription',
        },
      },
      {
        path: 'history',
        loadComponent: () =>
          import('./views/admin/admin-subscription/admin-subscription.component').then(
            (m) => m.AdminSubscriptionComponent,
          ),
        data: {
          subscriptionTab: 'history',
        },
      },
      {
        path: 'personalization',
        redirectTo: 'modules',
        pathMatch: 'full',
      },
      {
        path: 'users',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.USERS.TITLE',
          descriptionKey: 'ADMIN.IA.USERS.DESCRIPTION',
          tabs: [
            {
              route: '',
              labelKey: 'ADMIN.USERS.ALL',
              icon: 'fas fa-list',
              exact: true,
            },
            {
              route: 'new',
              labelKey: 'ADMIN.USERS.NEW',
              icon: 'fas fa-user-plus',
            },
          ],
        },
        children: [
          { path: '', component: TableUsersComponent },
          { path: 'new', component: FormUserComponent },
          { path: 'list', redirectTo: '', pathMatch: 'full' },
          { path: 'create', redirectTo: 'new', pathMatch: 'full' },
        ],
      },
      {
        path: 'users/invitations',
        redirectTo: 'invitations',
        pathMatch: 'full',
      },
      {
        path: 'invitations',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.USERS.INVITATION.PENDING_TITLE',
          descriptionKey: 'ADMIN.USERS.INVITATION.PENDING_DESCRIPTION',
        },
        children: [{ path: '', component: AdminUserInvitationsComponent }],
      },
      {
        path: 'users/:users_id',
        component: AdminUserDetailComponent,
        resolve: {
          user: adminUserResolver,
        },
        children: [
          { path: '', redirectTo: 'profile', pathMatch: 'full' },
          { path: 'profile', component: AdminUserProfileComponent },
          { path: 'employee', component: AdminUserEmployeeComponent },
          { path: 'access', component: AdminUserAccessComponent },
          { path: 'security', component: AdminUserSecurityComponent },
          { path: 'landing', component: AdminUserLandingComponent },
        ],
      },
      {
        path: 'roles',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.ROLES.TITLE',
          descriptionKey: 'ADMIN.IA.ROLES.DESCRIPTION',
          tabs: [
            {
              route: '',
              labelKey: 'ADMIN.ROLES.ALL',
              icon: 'fas fa-list',
              exact: true,
            },
            {
              route: 'new',
              labelKey: 'ADMIN.ROLES.ADD',
              icon: 'fas fa-plus',
            },
          ],
        },
        children: [
          { path: '', component: TableRolesComponent },
          { path: 'new', component: FormRoleComponent },
          { path: 'list', redirectTo: '', pathMatch: 'full' },
          { path: 'create', redirectTo: 'new', pathMatch: 'full' },
        ],
      },
      {
        path: 'roles/:roles_id',
        component: AdminRoleDetailComponent,
        resolve: {
          role: adminRoleResolver,
        },
        children: [
          { path: '', redirectTo: 'edit', pathMatch: 'full' },
          { path: 'edit', component: AdminRoleEditComponent },
          { path: 'permissions', component: AdminRolePermissionsComponent },
          { path: 'widgets', component: AdminRoleWidgetsComponent },
        ],
      },
      {
        path: 'roles/:roles_id/capabilities',
        redirectTo: 'roles/:roles_id/permissions',
        pathMatch: 'full',
      },
      {
        path: 'clinics',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.SITES.TITLE',
          descriptionKey: 'ADMIN.IA.SITES.DESCRIPTION',
          tabs: [
            {
              route: '',
              labelKey: 'ADMIN.SITES.ALL',
              icon: 'fas fa-list',
              exact: true,
            },
            {
              route: 'new',
              labelKey: 'ADMIN.SITES.NEW',
              icon: 'fas fa-plus',
            },
          ],
        },
        children: [
          { path: '', component: TableSitesComponent },
          { path: 'new', component: FormSiteComponent },
          { path: 'list', redirectTo: '', pathMatch: 'full' },
          { path: 'create', redirectTo: 'new', pathMatch: 'full' },
        ],
      },
      {
        path: 'clinics/:sites_id',
        redirectTo: 'clinics/:sites_id/general',
        pathMatch: 'full',
      },
      {
        path: 'clinics/:sites_id/config',
        redirectTo: 'clinics/:sites_id/general',
        pathMatch: 'full',
      },
      {
        path: 'clinics/:sites_id/general',
        loadComponent: () =>
          import('./views/admin/admin-site-config/admin-site-config.component').then(
            (m) => m.AdminSiteConfigComponent,
          ),
        resolve: {
          site: adminClinicResolver,
        },
        data: {
          tab: 'general',
        },
      },
      {
        path: 'clinics/:sites_id/clinical-history',
        loadComponent: () =>
          import('./views/admin/admin-site-config/admin-site-config.component').then(
            (m) => m.AdminSiteConfigComponent,
          ),
        resolve: {
          site: adminClinicResolver,
        },
        data: {
          tab: 'clinical-history',
        },
      },
      {
        path: 'clinics/:sites_id/calendars',
        loadComponent: () =>
          import('./views/admin/admin-site-config/admin-site-config.component').then(
            (m) => m.AdminSiteConfigComponent,
          ),
        resolve: {
          site: adminClinicResolver,
        },
        data: {
          tab: 'calendars',
        },
      },
      {
        path: 'clinics/:sites_id/treatments',
        loadComponent: () =>
          import('./views/admin/admin-site-config/admin-site-config.component').then(
            (m) => m.AdminSiteConfigComponent,
          ),
        resolve: {
          site: adminClinicResolver,
        },
        data: {
          tab: 'treatments',
        },
      },
      {
        path: 'clinics/:sites_id/products',
        loadComponent: () =>
          import('./views/admin/admin-site-config/admin-site-config.component').then(
            (m) => m.AdminSiteConfigComponent,
          ),
        canActivate: [WorkspaceAccessGuard],
        resolve: {
          site: adminClinicResolver,
        },
        data: {
          tab: 'products',
          requiredModule: 'sales',
        },
      },
      {
        path: 'clinics/:sites_id/booking',
        loadComponent: () =>
          import('./views/admin/admin-site-config/admin-site-config.component').then(
            (m) => m.AdminSiteConfigComponent,
          ),
        canActivate: [WorkspaceAccessGuard],
        resolve: {
          site: adminClinicResolver,
        },
        data: {
          tab: 'booking',
          requiredModule: 'online_booking',
        },
      },
      {
        path: 'clinics/:sites_id/reviews',
        loadComponent: () =>
          import('./views/admin/admin-site-config/admin-site-config.component').then(
            (m) => m.AdminSiteConfigComponent,
          ),
        canActivate: [WorkspaceAccessGuard],
        resolve: {
          site: adminClinicResolver,
        },
        data: {
          tab: 'reviews',
          requiredModule: 'online_booking',
        },
      },
      {
        path: 'clinics/:sites_id/email',
        loadComponent: () =>
          import('./views/admin/admin-site-config/admin-site-config.component').then(
            (m) => m.AdminSiteConfigComponent,
          ),
        resolve: {
          site: adminClinicResolver,
        },
        data: {
          tab: 'email',
        },
      },
      {
        path: 'clinics/:sites_id/reminders',
        loadComponent: () =>
          import('./views/admin/admin-site-config/admin-site-config.component').then(
            (m) => m.AdminSiteConfigComponent,
          ),
        resolve: {
          site: adminClinicResolver,
        },
        data: {
          tab: 'reminders',
        },
      },
      {
        path: 'clinics/:sites_id/visibility',
        loadComponent: () =>
          import('./views/admin/admin-site-config/admin-site-config.component').then(
            (m) => m.AdminSiteConfigComponent,
          ),
        resolve: {
          site: adminClinicResolver,
        },
        data: {
          tab: 'visibility',
        },
      },
      {
        path: 'clinics/:sites_id/calendars/new',
        component: AdminCalendarDetailComponent,
        resolve: {
          site: adminClinicResolver,
        },
      },
      {
        path: 'clinics/:sites_id/calendars/:calendar_id',
        component: AdminCalendarDetailComponent,
        resolve: {
          site: adminClinicResolver,
          calendar: adminCalendarResolver,
        },
      },
      {
        path: 'schedules/index',
        redirectTo: 'schedules',
        pathMatch: 'full',
      },
      {
        path: 'schedules',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.SCHEDULES.TITLE',
          descriptionKey: 'ADMIN.IA.SCHEDULES.DESCRIPTION',
        },
        children: [{ path: '', component: AdminSchedulesComponent }],
      },
      {
        path: 'nutrition-settings',
        loadComponent: () =>
          import('./views/admin/admin-nutrition-settings/admin-nutrition-settings.component').then(
            (m) => m.AdminNutritionSettingsComponent,
          ),
        canActivate: [WorkspaceAccessGuard],
        data: {
          requiredModule: 'nutrition',
        },
      },
      {
        path: 'patients',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.PATIENTS.TITLE',
          descriptionKey: 'ADMIN.IA.PATIENTS.DESCRIPTION',
          tabs: [
            {
              route: '',
              labelKey: 'ADMIN.PATIENTS.ALL',
              icon: 'fas fa-list',
              exact: true,
            },
            {
              route: 'new',
              labelKey: 'ADMIN.PATIENTS.ADD',
              icon: 'fas fa-user-plus',
            },
            {
              route: 'metadata',
              labelKey: 'ADMIN.PATIENTS.METADATA.TITLE',
              icon: 'fas fa-database',
              exact: true,
            },
            {
              route: 'metadata/new',
              labelKey: 'ADMIN.PATIENTS.METADATA.TITLE',
              icon: 'fas fa-plus-square',
              exact: true,
            },
          ],
        },
        children: [
          { path: '', component: TablePatientsComponent },
          { path: 'new', component: FormPatientComponent },
          { path: 'list', redirectTo: '', pathMatch: 'full' },
          { path: 'create', redirectTo: 'new', pathMatch: 'full' },
          {
            path: 'metadata/new',
            component: FormMetadataPatientsComponent,
          },
          { path: 'metadata', component: TableMetadataPatientsComponent },
          {
            path: 'metadata/list',
            redirectTo: 'metadata',
            pathMatch: 'full',
          },
          {
            path: 'metadata/create',
            redirectTo: 'metadata/new',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'patients/:patients_id',
        redirectTo: 'patients/:patients_id/profile',
        pathMatch: 'full',
      },
      {
        path: 'patients/:patients_id/profile',
        component: AdminPatientManageComponent,
        resolve: {
          patient: adminPatientResolver,
        },
        data: { tab: 'profile' },
      },
      {
        path: 'patients/:patients_id/metadata',
        component: AdminPatientManageComponent,
        resolve: {
          patient: adminPatientResolver,
        },
        data: { tab: 'metadata' },
      },
      {
        path: 'patients/:patients_id/history',
        component: AdminPatientManageComponent,
        resolve: {
          patient: adminPatientResolver,
        },
        data: { tab: 'history' },
      },
      {
        path: 'patients/:patients_id/sites',
        component: AdminPatientManageComponent,
        resolve: {
          patient: adminPatientResolver,
        },
        data: { tab: 'sites' },
      },
      {
        path: 'patients/:patients_id/appointments',
        component: AdminPatientManageComponent,
        resolve: {
          patient: adminPatientResolver,
        },
        data: { tab: 'appointments' },
      },
      {
        path: 'patients/:patients_id/treatments',
        component: AdminPatientManageComponent,
        resolve: {
          patient: adminPatientResolver,
        },
        data: { tab: 'treatments' },
      },
      {
        path: 'patients/:patients_id/manage',
        redirectTo: 'patients/:patients_id/profile',
        pathMatch: 'full',
      },
      {
        path: 'clinical-history-structures',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.CLINICAL_HISTORY_STRUCTURES.TITLE',
          descriptionKey: 'ADMIN.IA.CLINICAL_HISTORY_STRUCTURES.DESCRIPTION',
          tabs: [
            {
              route: '',
              labelKey: 'ADMIN.CLINICAL_HISTORY_STRUCTURES.ALL',
              icon: 'fas fa-list',
              exact: true,
            },
            {
              route: 'new',
              labelKey: 'ADMIN.CLINICAL_HISTORY_STRUCTURES.NEW',
              icon: 'fas fa-plus',
            },
            {
              route: 'templates',
              labelKey: 'ADMIN.CLINICAL_HISTORY_STRUCTURES.TEMPLATES',
              icon: 'fas fa-file-lines',
            },
          ],
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./views/admin/admin-clinical-history-structures/admin-clinical-history-structures.component').then(
                (m) => m.AdminClinicalHistoryStructuresComponent,
              ),
          },
          {
            path: 'new',
            loadComponent: () =>
              import('./views/admin/admin-clinical-history-structure-detail/admin-clinical-history-structure-detail.component').then(
                (m) => m.AdminClinicalHistoryStructureDetailComponent,
              ),
          },
          {
            path: 'templates',
            loadComponent: () =>
              import('./views/admin/admin-clinical-history-templates/admin-clinical-history-templates.component').then(
                (m) => m.AdminClinicalHistoryTemplatesComponent,
              ),
          },
        ],
      },
      {
        path: 'clinical-history-structures/:structure_id',
        loadComponent: () =>
          import('./views/admin/admin-clinical-history-structure-detail/admin-clinical-history-structure-detail.component').then(
            (m) => m.AdminClinicalHistoryStructureDetailComponent,
          ),
      },
      {
        path: 'treatments',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.TREATMENTS.TITLE',
          descriptionKey: 'ADMIN.IA.TREATMENTS.DESCRIPTION',
          tabs: [
            {
              route: '',
              labelKey: 'ADMIN.TREATMENTS.ALL',
              icon: 'fas fa-list',
              exact: true,
            },
            {
              route: 'new',
              labelKey: 'ADMIN.TREATMENTS.ADD',
              icon: 'fas fa-plus',
            },
            {
              route: 'categories',
              labelKey: 'ADMIN.TREATMENTS.ALL.CATEGORIES',
              icon: 'fas fa-layer-group',
              exact: true,
            },
            {
              route: 'categories/new',
              labelKey: 'ADMIN.TREATMENTS.ADD.CATEGORIES',
              icon: 'fas fa-folder-plus',
              exact: true,
            },
          ],
        },
        children: [
          { path: '', component: TableTreatmentsComponent },
          { path: 'new', component: FormTreatmentComponent },
          {
            path: 'categories',
            component: TableCategoriesTreatmentsComponent,
          },
          {
            path: 'categories/new',
            component: FormCategoryTreatmentsComponent,
          },
          { path: 'list', redirectTo: '', pathMatch: 'full' },
          { path: 'create', redirectTo: 'new', pathMatch: 'full' },
          {
            path: 'categories/list',
            redirectTo: 'categories',
            pathMatch: 'full',
          },
          {
            path: 'categories/create',
            redirectTo: 'categories/new',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'treatments/categories/:category_id',
        component: AdminTreatmentCategoryDetailComponent,
        resolve: {
          treatmentCategory: adminTreatmentCategoryResolver,
        },
      },
      {
        path: 'treatments/:treatments_id',
        component: AdminTreatmentDetailComponent,
        resolve: {
          treatment: adminTreatmentResolver,
        },
      },
      {
        path: 'products',
        component: AdminSectionComponent,
        canActivate: [WorkspaceAccessGuard],
        data: {
          requiredModule: 'sales',
          titleKey: 'ADMIN.IA.PRODUCTS.TITLE',
          descriptionKey: 'ADMIN.IA.PRODUCTS.DESCRIPTION',
          tabs: [
            {
              route: '',
              labelKey: 'ADMIN.PRODUCTS.ALL',
              icon: 'fas fa-list',
              exact: true,
            },
            {
              route: 'new',
              labelKey: 'ADMIN.PRODUCTS.ADD',
              icon: 'fas fa-plus',
            },
            {
              route: 'categories',
              labelKey: 'ADMIN.PRODUCTS.ALL.CATEGORIES',
              icon: 'fas fa-layer-group',
              exact: true,
            },
            {
              route: 'categories/new',
              labelKey: 'ADMIN.PRODUCTS.ADD.CATEGORIES',
              icon: 'fas fa-folder-plus',
              exact: true,
            },
          ],
        },
        children: [
          { path: '', component: TableProductsComponent },
          { path: 'new', component: FormProductsComponent },
          { path: 'categories', component: TableCategoriesProductsComponent },
          { path: 'categories/new', component: FormCategoryProductsComponent },
          { path: 'list', redirectTo: '', pathMatch: 'full' },
          { path: 'create', redirectTo: 'new', pathMatch: 'full' },
          {
            path: 'categories/list',
            redirectTo: 'categories',
            pathMatch: 'full',
          },
          {
            path: 'categories/create',
            redirectTo: 'categories/new',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'products/categories/:category_id',
        component: AdminProductCategoryDetailComponent,
        canActivate: [WorkspaceAccessGuard],
        resolve: {
          productCategory: adminProductCategoryResolver,
        },
        data: {
          requiredModule: 'sales',
        },
      },
      {
        path: 'products/:products_id',
        component: AdminProductDetailComponent,
        canActivate: [WorkspaceAccessGuard],
        resolve: {
          product: adminProductResolver,
        },
        data: {
          requiredModule: 'sales',
        },
      },
      {
        path: 'jobs',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.JOBS.TITLE',
          descriptionKey: 'ADMIN.IA.JOBS.DESCRIPTION',
          tabs: [
            {
              route: '',
              labelKey: 'ADMIN.JOBS.ALL',
              icon: 'fas fa-list',
              exact: true,
            },
            {
              route: 'new',
              labelKey: 'ADMIN.JOBS.ADD',
              icon: 'fas fa-plus',
            },
          ],
        },
        children: [
          { path: '', component: TableJobsComponent },
          { path: 'new', component: FormJobComponent },
          { path: 'list', redirectTo: '', pathMatch: 'full' },
          { path: 'create', redirectTo: 'new', pathMatch: 'full' },
        ],
      },
      {
        path: 'jobs/:jobs_id',
        component: AdminJobDetailComponent,
        resolve: {
          job: adminJobResolver,
        },
      },
      {
        path: 'marketing-channels',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.MARKETING.TITLE',
          descriptionKey: 'ADMIN.IA.MARKETING.DESCRIPTION',
          tabs: [
            {
              route: '',
              labelKey: 'ADMIN.MARKETING.CHANNELS.ALL',
              icon: 'fas fa-list',
              exact: true,
            },
            {
              route: 'new',
              labelKey: 'ADMIN.MARKETING.CHANNELS.ADD',
              icon: 'fas fa-plus',
            },
          ],
        },
        children: [
          { path: '', component: TableMarketingChannelsComponent },
          { path: 'new', component: FormMarketingChannelComponent },
          { path: 'list', redirectTo: '', pathMatch: 'full' },
          { path: 'create', redirectTo: 'new', pathMatch: 'full' },
        ],
      },
      {
        path: 'marketing-channels/:channels_id',
        component: AdminMarketingChannelDetailComponent,
        resolve: {
          marketingChannel: adminMarketingResolver,
        },
      },
      {
        path: 'logs',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.LOGS.TITLE',
          descriptionKey: 'ADMIN.IA.LOGS.DESCRIPTION',
          tabs: [
            {
              route: '',
              labelKey: 'ADMIN.LOG.ALL',
              icon: 'fas fa-list',
              exact: true,
            },
          ],
        },
        children: [
          { path: '', component: TableLogsComponent },
          { path: 'list', redirectTo: '', pathMatch: 'full' },
        ],
      },
      {
        path: 'data',
        component: AdminSectionComponent,
        data: {
          titleKey: 'ADMIN.IA.DATA.TITLE',
          descriptionKey: 'ADMIN.IA.DATA.DESCRIPTION',
          tabs: [
            {
              route: 'export/appointments',
              labelKey: 'ADMIN.DATA.EXPORT.TITLE',
              icon: 'fas fa-file-export',
              exact: false,
            },
            {
              route: 'export/patients',
              labelKey: 'ADMIN.DATA.EXPORT.PATIENTS',
              icon: 'fas fa-hospital-user',
              exact: false,
            },
          ],
        },
        children: [
          { path: '', redirectTo: 'export/appointments', pathMatch: 'full' },
          {
            path: 'export/appointments',
            component: ExportAppointmentsComponent,
          },
          { path: 'export/patients', component: ExportPatientsComponent },
        ],
      },
      {
        path: '**',
        redirectTo: 'users',
      },
    ],
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];
